import { X, ExternalLink, Bookmark, BookmarkCheck } from "@/components/Icons";

export default function RecordDetail({ record, onClose, onSave, onUnsave, isSaved }) {
  if (!record) return null;

  const handleSaveClick = () => {
    if (isSaved) {
      onUnsave?.(record.naId || record.naid);
    } else {
      onSave?.(record);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="record-detail-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="record-detail-content">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#E5E2D9]">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs uppercase tracking-widest text-[#718096]">
              Record Detail
            </span>
            <span className="font-mono text-xs text-[#718096]">
              / NAID {record.naId || record.naid}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-ghost"
              onClick={handleSaveClick}
              data-testid="detail-save-btn"
            >
              {isSaved ? (
                <>
                  <BookmarkCheck size={16} className="text-[#B85C38]" />
                  <span className="text-[#B85C38]">Saved</span>
                </>
              ) : (
                <>
                  <Bookmark size={16} />
                  <span>Save</span>
                </>
              )}
            </button>
            <a
              href={`https://catalog.archives.gov/id/${record.naId || record.naid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1A2B3C] text-white font-mono text-xs hover:bg-[#2A405A] transition-colors"
              data-testid="detail-nara-link"
            >
              <ExternalLink size={13} />
              <span>View on NARA</span>
            </a>
            <button className="btn-ghost p-2" onClick={onClose} data-testid="detail-close-btn">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="p-5 border-b border-[#E5E2D9] bg-[#F9F7F1]">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#1A1A1A] leading-tight" data-testid="detail-title">
            {record.title}
          </h2>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {record.types && record.types.map((t) => (
              <span key={t} className="type-badge">{t}</span>
            ))}
            {record.recordType && (
              <span className="type-badge">{record.recordType}</span>
            )}
            {record.hasOnlineContent && (
              <span className="type-badge" style={{ borderColor: "#B85C38", color: "#B85C38" }}>
                Available Online
              </span>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className="p-5">
          {record.description && (
            <div className="mb-5">
              <h4 className="font-mono text-xs uppercase tracking-widest text-[#718096] mb-2">
                Scope & Content
              </h4>
              <p className="text-sm text-[#4A5568] leading-relaxed" data-testid="detail-description">
                {record.description}
              </p>
            </div>
          )}

          <div className="border border-[#E5E2D9]">
            {record.dateRange && (
              <div className="metadata-row px-4">
                <span className="metadata-key">Date</span>
                <span className="metadata-value" data-testid="detail-date">{record.dateRange}</span>
              </div>
            )}

            <div className="metadata-row px-4">
              <span className="metadata-key">NAID</span>
              <span className="metadata-value font-mono">{record.naId || record.naid}</span>
            </div>

            {record.creators && record.creators.length > 0 && (
              <div className="metadata-row px-4">
                <span className="metadata-key">Creator(s)</span>
                <span className="metadata-value">{record.creators.join(", ")}</span>
              </div>
            )}

            {record.contributingArchive && (
              <div className="metadata-row px-4">
                <span className="metadata-key">Archive</span>
                <span className="metadata-value" data-testid="detail-archive">{record.contributingArchive}</span>
              </div>
            )}

            {record.hierarchy && Object.entries(record.hierarchy).map(([key, value]) =>
              value ? (
                <div className="metadata-row px-4" key={key}>
                  <span className="metadata-key">{key.replace(/([A-Z])/g, " $1").trim()}</span>
                  <span className="metadata-value">{value}</span>
                </div>
              ) : null
            )}
          </div>

          {/* Thumbnail */}
          {record.thumbnailUrl && (
            <div className="mt-5">
              <h4 className="font-mono text-xs uppercase tracking-widest text-[#718096] mb-3">
                Digital Object
              </h4>
              <div className="border border-[#E5E2D9] p-4 bg-[#F9F7F1] text-center">
                <img
                  src={record.thumbnailUrl}
                  alt={record.title}
                  className="max-h-64 mx-auto object-contain"
                  data-testid="detail-thumbnail"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
                <p className="text-xs text-[#718096] mt-2 font-mono hidden">Image unavailable</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
